import { PaginationControl } from "@/components";
import { IntelListItem } from "@/pages/intel/intel-listings/components/IntelListItem";
import {
  allIntelAuthorUsernamesLoader,
  intelLoader,
} from "@/pages/intel/loaders/intel-loaders";
import { useAccount, useAsync } from "@/shared/hooks";

import { Filter, SkeletonText } from "@suns/design-system";
import { SunsApiError } from "@suns/api";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { IntelQueryParams, IntelTag } from "@suns/api/generated-client/apollo";
import { Flex, Text } from "@suns/design-system";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Author } from "../EntityReportListings/EntityReportListings";
import { IntelTagLabels } from "../../pages/players/players-const";

export function EntityIntelListings() {
  const PAGE_SIZE = 10;
  const { playerId, teamId, agentId, personnelId } = useParams();
  const [publishedPage, setPublishedPage] = useState(0);
  const account = useAccount();
  const { hasBosaAccess } = account;

  const [selectedIntelAuthors, setSelectedIntelAuthors] = useState<string[]>(
    []
  );

  const [tags, setTags] = useState<string[]>([]);

  const {
    loading: publishedLoading,
    response: publishedResponse,
    error: publishedError,
    refresh: refreshPublished,
  } = useAsync(intelLoader, {
    status: IntelQueryParams.status.PUBLISHED,
    offset: publishedPage * PAGE_SIZE,
    limit: PAGE_SIZE,
    playerIds: playerId ? [Number(playerId)] : undefined,
    teamIds: teamId ? [Number(teamId)] : undefined,
    agentIds: agentId ? [Number(agentId)] : undefined,
    personnelIds: personnelId ? [Number(personnelId)] : undefined,
    tags: tags,
    authorUsername: selectedIntelAuthors,
    includeExecIntel: hasBosaAccess,
  });

  const {
    response: intelAuthorsResponse,
    loading: intelAuthorsLoading,
    error: intelAuthorsError,
  } = useAsync(allIntelAuthorUsernamesLoader);

  const intelAuthors = intelAuthorsResponse?.map((author: Author) => ({
    key: author.authorUsername,
    name: author.authorName,
  }));

  const handleSetPage = (page: number) => {
    setPublishedPage(page - 1);
  };

  const currentPublishedPage = Math.floor(
    (publishedResponse?.offset || 0) / (publishedResponse?.limit || 1)
  );

  useEffect(() => {
    if (publishedPage !== currentPublishedPage) {
      window.scrollTo({ top: 0 });
      refreshPublished();
    }
  }, [publishedPage, currentPublishedPage, refreshPublished]);

  const handleTagClick = (tag: string) => {
    const newTags = tags.includes(tag)
      ? tags.filter((t) => t !== tag)
      : [...tags, tag];
    setTags(newTags);
  };

  function handleReportsFilterChange(values: string[]) {
    setTags(values);
  }

  const intelTagOptions = Object.values(IntelTag).map((tag) => ({
    key: tag,
    name: IntelTagLabels[tag],
    value: tag,
  }));

  if (publishedError) {
    throw new SunsApiError("Error loading the published intel.", {
      cause: publishedError,
    });
  }

  if (intelAuthorsError) {
    throw new SunsApiError("Error loading the intel authors.", {
      cause: intelAuthorsError,
    });
  }

  return (
    <>
      <Flex direction="right" align="center" gap="md" className="mb-2">
        <Filter
          value={tags}
          title="Tags"
          onChange={handleReportsFilterChange}
          options={intelTagOptions}
        />
        <Filter
          avoidCollisions={false}
          title={intelAuthorsLoading ? "Loading..." : "Author"}
          value={selectedIntelAuthors || []}
          onChange={(value) => setSelectedIntelAuthors(value as string[])}
          options={intelAuthors ?? []}
        />
      </Flex>
      {publishedLoading ? (
        <IntelListingsLoading />
      ) : (
        <>
          {publishedResponse && publishedResponse?.intel?.length > 0 ? (
            <Flex direction="down" className="divide-y-2">
              {publishedResponse?.intel.map((intel) => (
                <IntelListItem
                  hideButtons
                  key={intel.id}
                  className="py-4"
                  intel={intel}
                  onTagClick={handleTagClick}
                />
              ))}
            </Flex>
          ) : (
            <Text className="py-10 text-center" size="lg" muted>
              No intel found.
            </Text>
          )}
          <PaginationControl
            {...publishedResponse}
            onPageClicked={handleSetPage}
          />
        </>
      )}
    </>
  );
}

function IntelListingsLoading() {
  return (
    <Flex direction="down" gap="lg">
      <SkeletonText />
      <SkeletonText />
    </Flex>
  );
}
